import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileText, Plus, Download, Eye, Loader2 } from 'lucide-react';

export default function AgreementsPage() {
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [genForm, setGenForm] = useState({ client_id: '', investment_amount: '' });
  const [generating, setGenerating] = useState(false);

  const fetchData = async () => {
    try {
      const [aRes, cRes] = await Promise.all([
        api.get('/agreements'),
        api.get('/clients').catch(() => ({ data: [] }))
      ]);
      setAgreements(aRes.data);
      setClients(cRes.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleGenerate = async () => {
    if (!genForm.client_id) { toast.error('Select a client'); return; }
    setGenerating(true);
    try {
      await api.post('/agreements/generate', {
        client_id: genForm.client_id,
        investment_amount: parseFloat(genForm.investment_amount) || 0
      });
      toast.success('Agreement generated');
      setDialogOpen(false);
      setGenForm({ client_id: '', investment_amount: '' });
      fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const handleDownload = async (id) => {
    try {
      const res = await api.get(`/agreements/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `agreement-${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error('Download failed'); }
  };


  return (
    <div className="space-y-6" data-testid="agreements-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary tracking-tight">Agreements</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Generate & manage client investment agreements</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg" data-testid="generate-agreement-btn">
          <Plus size={16} className="mr-2" /> Generate Agreement
        </Button>
      </div>

      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="agreements-table">
              <thead><tr className="border-b border-brand-border bg-brand-bg/50">
                {['Agreement #', 'Client', 'Client ID', 'Investment', 'Sharing', 'Status', 'Date', 'Actions'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr> :
                 agreements.length === 0 ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">No agreements yet. Generate one for a client.</td></tr> :
                 agreements.map(a => (
                  <tr key={a.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-3 px-4 font-mono text-xs font-semibold text-brand-primary">{a.agreement_number}</td>
                    <td className="py-3 px-4 text-brand-text-primary font-medium">{a.client_name}</td>
                    <td className="py-3 px-4 font-mono text-xs">{a.client_client_id || '-'}</td>
                    <td className="py-3 px-4 font-medium">${Number(a.investment_amount || 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-xs text-brand-text-secondary">{a.investor_pct}/{a.trader_pct}/{a.company_pct}</td>
                    <td className="py-3 px-4">
                      <Badge className={`text-xs border-0 ${a.status === 'signed' ? 'bg-brand-success/15 text-brand-success' : 'bg-amber-100 text-amber-700'}`}>{a.status}</Badge>
                    </td>
                    <td className="py-3 px-4 text-brand-text-secondary text-xs">{a.agreement_date}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/agreements/${a.id}`)} className="h-8 w-8 text-brand-primary" data-testid={`view-agr-${a.id}`}><Eye size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDownload(a.id)} className="h-8 w-8" data-testid={`dl-agr-${a.id}`}><Download size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="generate-agreement-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Generate Agreement</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={genForm.client_id} onValueChange={v => setGenForm({...genForm, client_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-lg" data-testid="agr-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.client_id || c.email})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Investment Amount ($)</Label>
              <Input type="number" value={genForm.investment_amount} onChange={e => setGenForm({...genForm, investment_amount: e.target.value})} placeholder="0.00" className="bg-white border-brand-border rounded-lg" data-testid="agr-amount-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating} className="bg-brand-primary text-white rounded-lg" data-testid="agr-generate-btn">
              {generating ? <Loader2 size={16} className="animate-spin mr-2" /> : <FileText size={16} className="mr-2" />} Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
